import { useEffect, useState, type CSSProperties } from 'react'
import { weekdayHeading } from '../../lib/datetime.js'

/**
 * Diana's week (Studio-Calendar.png): seven day columns, an hour per 52px
 * row, and four kinds of block on them —
 *
 *   lesson   pale blue, a dot in the student's group colour
 *   request  soft gold: someone asked from the website, she has to answer
 *   open     dashed: „Wolne · N wolne godziny”, offered on the website
 *   off      hatched: „Nie pracuję”
 *
 * Open and off are the BACKGROUND of the day: they sit under the hour cells,
 * so a click on them is a click on that hour, and hours painted in select
 * mode show through them. Lessons and requests sit on top.
 *
 * The public booking page has its own grid (components/WeekGrid.tsx), whose
 * look must not change; the painting gesture used to live there and moved
 * here with the calendar.
 *
 * Like that one it knows nothing about timezones or bookings: callers hand
 * it ISO days and minutes since midnight already resolved in Diana's zone.
 */

export type Cell = { day: string; hour: number }

export type BlockKind = 'lesson' | 'request' | 'open' | 'off'

export type Block = {
  id: string
  /** 'YYYY-MM-DD' — must be one of `days`, otherwise it is not drawn. */
  day: string
  startMin: number
  endMin: number
  kind: BlockKind
  title: string
  sub?: string
  /** Lesson: the colour of the dot (the student's group). */
  dot?: string
  /** Open: put a „waiting” dot before the sub line — hours the website does not offer. */
  warn?: boolean
  /** Off: the hatching. */
  style?: CSSProperties
  /** Read out and shown on hover: who and when. */
  label?: string
  onClick?: () => void
}

type Props = {
  days: string[]
  startHour: number
  endHour: number
  today?: string
  blocks: Block[]
  /** An hour that is already behind us — drawn with a faint wash. */
  isPast?: (day: string, hour: number) => boolean
  onCellClick: (day: string, hour: number) => void
  /**
   * Painting mode. Clicking or dragging marks a rectangle of hours instead
   * of opening one — so "Monday to Friday, 16:00–18:00" is one gesture.
   */
  selectMode: boolean
  isSelected: (day: string, hour: number) => boolean
  /**
   * Hours that may be painted. A cell this says no to cannot start a
   * gesture, is never highlighted, and drops out of one that sweeps over
   * it — so a drag from Monday into next week still works, and picks up
   * nothing that cannot be acted on.
   */
  canSelect: (day: string, hour: number) => boolean
  /**
   * ⌘- or Ctrl-click on an hour, in either mode. Picking a handful of
   * scattered hours is a different gesture from sweeping a block, and
   * having to find a mode button first is one step too many.
   */
  onPickClick: (day: string, hour: number) => void
  /** A gesture that landed entirely on hours `canSelect` refused. */
  onNothingSelectable: () => void
  /** Called once when the gesture ends, never mid-drag. */
  onSelectRange: (cells: Cell[], select: boolean) => void
}

/** px per hour — the mockup's row. */
const HOUR = 52
const HOUR_LINES = `repeating-linear-gradient(to bottom, transparent 0 ${HOUR - 1}px, var(--color-class-line) ${HOUR - 1}px ${HOUR}px)`

const BLOCK_LOOK: Record<BlockKind, string> = {
  lesson: 'bg-class-field-soft text-class-ink-soft',
  request: 'bg-class-gold-100 text-class-gold-text shadow-[inset_0_0_0_1px_var(--color-class-gold-line)]',
  open: 'border border-dashed border-[#b9d4ea] bg-white/60 text-class-ink-faint',
  off: 'text-class-ink-faint',
}

type Drag = { fromDay: number; fromHour: number; toDay: number; toHour: number; select: boolean }

export function StudioWeekGrid({
  days,
  startHour,
  endHour,
  today,
  blocks,
  isPast,
  onCellClick,
  selectMode,
  isSelected,
  canSelect,
  onPickClick,
  onNothingSelectable,
  onSelectRange,
}: Props) {
  const hours = Array.from({ length: endHour - startHour }, (_, i) => startHour + i)
  const gridStart = startHour * 60
  const gridEnd = endHour * 60

  const [drag, setDrag] = useState<Drag | null>(null)

  /**
   * The gesture is committed on pointerup anywhere — including outside the
   * grid, which is where a fast drag usually ends. Until then the rectangle
   * is only a preview, so the parent's selection never flickers mid-drag.
   */
  useEffect(() => {
    if (!drag) return
    const finish = () => {
      const dayFrom = Math.min(drag.fromDay, drag.toDay)
      const dayTo = Math.max(drag.fromDay, drag.toDay)
      const hourFrom = Math.min(drag.fromHour, drag.toHour)
      const hourTo = Math.max(drag.fromHour, drag.toHour)

      const cells: Cell[] = []
      let refused = 0
      for (let d = dayFrom; d <= dayTo; d++) {
        for (let h = hourFrom; h <= hourTo; h++) {
          const day = days[d]
          if (!day) continue
          if (canSelect(day, h)) cells.push({ day, hour: h })
          else refused++
        }
      }
      if (cells.length > 0) onSelectRange(cells, drag.select)
      // A sweep that lands entirely on refused hours must say so rather than
      // look like a click that missed.
      else if (refused > 0) onNothingSelectable()
      setDrag(null)
    }

    window.addEventListener('pointerup', finish)
    window.addEventListener('pointercancel', finish)
    return () => {
      window.removeEventListener('pointerup', finish)
      window.removeEventListener('pointercancel', finish)
    }
  }, [drag, days, canSelect, onSelectRange, onNothingSelectable])

  const inDragRect = (dayIndex: number, hour: number) =>
    drag !== null &&
    dayIndex >= Math.min(drag.fromDay, drag.toDay) &&
    dayIndex <= Math.max(drag.fromDay, drag.toDay) &&
    hour >= Math.min(drag.fromHour, drag.toHour) &&
    hour <= Math.max(drag.fromHour, drag.toHour)

  const place = (b: Block): { style: CSSProperties; compact: boolean } => {
    const top = Math.max(b.startMin, gridStart) - gridStart
    const bottom = Math.min(b.endMin, gridEnd) - gridStart
    const height = ((bottom - top) / 60) * HOUR - 4
    return { style: { top: (top / 60) * HOUR + 2, height }, compact: height < 40 }
  }

  const drawBlock = (b: Block) => {
    const { style, compact } = place(b)
    const front = b.kind === 'lesson' || b.kind === 'request'
    const cls = [
      'absolute inset-x-1 flex flex-col overflow-hidden rounded-[10px] px-2.5 text-left font-class-text text-class-label leading-[1.3]',
      compact ? 'py-[3px]' : 'py-1.5',
      BLOCK_LOOK[b.kind],
      // The background of the day never takes a click: the hour under it does.
      front ? 'z-10' : 'pointer-events-none',
      // A lesson must not swallow the drag that crosses it, but it stays on
      // top of the selection: she has to see that the hour she is blocking
      // has a lesson in it. Blocking never cancels one.
      front && selectMode ? 'pointer-events-none opacity-90' : '',
      front && b.onClick && !selectMode ? 'cursor-pointer border-0 transition hover:shadow-[inset_0_0_0_1px_var(--color-class-sky-700)]' : '',
    ].join(' ')

    // Too short for two lines (a 30-minute lesson): name and what, on one.
    const inlineSub = compact && b.sub ? <span className="font-normal opacity-90"> · {b.sub}</span> : null
    const head =
      b.kind === 'lesson' ? (
        <span className="flex min-w-0 shrink-0 items-center gap-1.5 font-semibold whitespace-nowrap text-class-ink">
          <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: b.dot ?? 'var(--color-class-sky-700)' }} />
          <span className="truncate">
            {b.title}
            {inlineSub}
          </span>
        </span>
      ) : (
        <span className={['block shrink-0 truncate font-semibold', b.kind === 'open' ? 'text-class-sky-700' : ''].join(' ')}>
          {b.title}
          {inlineSub}
        </span>
      )
    const sub = b.sub && !compact ? (
      <span className="flex min-w-0 shrink-0 items-center gap-1.5 whitespace-nowrap">
        {b.warn && <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-class-wait" />}
        <span className="truncate">{b.sub}</span>
      </span>
    ) : null

    const fullStyle = { ...style, ...b.style }
    return b.onClick && front && !selectMode ? (
      <button key={b.id} type="button" onClick={b.onClick} style={fullStyle} className={cls} aria-label={b.label} title={b.label}>
        {head}
        {sub}
      </button>
    ) : (
      <div key={b.id} style={fullStyle} className={cls} title={b.label}>
        {head}
        {sub}
      </div>
    )
  }

  const hh = (h: number) => `${h}:00`
  const cellLabel = (day: string, h: number) => `${day} ${String(h).padStart(2, '0')}:00`

  return (
    <div className="overflow-x-auto pb-2">
      <div className="min-w-[640px]">
        {/* ── headings: „pon. 19” ───────────────────────────────────── */}
        <div className="grid grid-cols-[56px_repeat(7,minmax(0,1fr))]">
          <div />
          {days.map((day) => {
            const { weekday, day: num } = weekdayHeading(day)
            const isToday = day === today
            return (
              <div key={day} className="relative flex items-center px-1 pb-3 text-class-label font-semibold text-class-ink-faint">
                {weekday}
                <span
                  className={[
                    'ml-1 inline-flex items-center justify-center font-class-display text-class-subheading font-medium leading-none',
                    isToday ? 'h-7 min-w-7 rounded-full bg-class-sky-200 px-1.5 text-class-sky-700' : 'text-class-ink',
                  ].join(' ')}
                >
                  {num}
                </span>
                {isToday && <span className="sr-only">(dziś)</span>}
              </div>
            )
          })}
        </div>

        {/* ── body ─────────────────────────────────────────────────── */}
        <div
          className={[
            'grid grid-cols-[56px_repeat(7,minmax(0,1fr))] border-t border-r border-class-line',
            // Painting must not scroll the page under the finger, and the
            // drag must not turn every cell into highlighted text.
            selectMode ? 'touch-none select-none' : '',
          ].join(' ')}
        >
          {/* hour labels, each on its line */}
          <div aria-hidden className="relative">
            {hours.map((h) => (
              <div key={h} style={{ height: HOUR }} className="-translate-y-2 pr-3 text-right text-class-label text-class-ink-faint">
                {hh(h)}
              </div>
            ))}
            <span className="absolute right-3 bottom-0 translate-y-2 text-class-label text-class-ink-faint">{hh(endHour)}</span>
          </div>

          {days.map((day, dayIndex) => {
            const mine = blocks.filter((b) => b.day === day && b.endMin > gridStart && b.startMin < gridEnd)
            return (
              <div key={day} className="relative border-l border-class-line" style={{ backgroundImage: HOUR_LINES }}>
                {/* the background of the day: open hours and time off */}
                {mine.filter((b) => b.kind === 'open' || b.kind === 'off').map(drawBlock)}

                {/* the hours themselves — transparent, above the background */}
                {hours.map((h) => {
                  const past = isPast?.(day, h) ? 'bg-class-line/45' : ''

                  if (selectMode) {
                    const canPick = canSelect(day, h)
                    const picked = isSelected(day, h) && canPick
                    const previewing = canPick && inDragRect(dayIndex, h)
                    // While dragging over it, a cell shows what it is about to
                    // become, not what it is.
                    const on = previewing ? drag!.select : picked
                    return (
                      <button
                        key={h}
                        type="button"
                        onPointerDown={(e) => {
                          e.preventDefault()
                          // Silently ignoring the press is what this whole change
                          // is against: say why nothing happened.
                          if (!canPick) return onNothingSelectable()
                          setDrag({
                            fromDay: dayIndex,
                            fromHour: h,
                            toDay: dayIndex,
                            toHour: h,
                            // Starting on a selected cell erases, starting on
                            // an empty one paints — the spreadsheet gesture.
                            select: !picked,
                          })
                        }}
                        onPointerEnter={() => setDrag((d) => (d ? { ...d, toDay: dayIndex, toHour: h } : d))}
                        style={{ height: HOUR }}
                        aria-label={cellLabel(day, h)}
                        aria-pressed={on}
                        aria-disabled={!canPick}
                        // A picked cell drops its usual background entirely rather
                        // than layering on top of it: two background utilities in
                        // one class list are settled by stylesheet order, not by
                        // the order written here, and the selection lost.
                        className={[
                          'relative block w-full cursor-pointer border-0 p-0 text-left',
                          on
                            ? 'bg-class-sky-200/85 shadow-[inset_0_0_0_1px_var(--color-class-sky-700)]'
                            : canPick
                              ? `hover:bg-class-sky-200/40 ${past}`
                              : `cursor-not-allowed ${past}`,
                        ].join(' ')}
                      />
                    )
                  }

                  return (
                    <button
                      key={h}
                      type="button"
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey) return onPickClick(day, h)
                        onCellClick(day, h)
                      }}
                      style={{ height: HOUR }}
                      aria-label={cellLabel(day, h)}
                      className={['relative block w-full cursor-pointer border-0 p-0 text-left transition-colors hover:bg-class-field-soft/70', past].join(' ')}
                    />
                  )
                })}

                {/* lessons and requests, on top */}
                {mine.filter((b) => b.kind === 'lesson' || b.kind === 'request').map(drawBlock)}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
